import {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  List, Divider, ListItem,
  Paper,
} from '@material-ui/core';
import _, { isNumber } from 'lodash';
import NumberFormat from 'react-number-format';
import { BiExpand } from 'react-icons/bi';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import {
  FaCheck, FaChevronDown, FaChevronUp, FaTimes,
  FaRegQuestionCircle,
} from 'react-icons/fa';
import clsx from 'clsx';

import useStyles from './styles';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import {
  addToTrip, removeFromTrip, saveTripRequest, setSelfBookingAllowed,
} from '../../../../../store/slices/trip';
import { getRoomDescriptionRequest, getRoomDetailRequest, setCurrentRateDetailCode } from '../../../../../store/slices/hotel';
import HotelAddUpdateToBrochure from '../../../AddUpdateBrochure/HotelAddUpdateToBrochure';
import {
  HotelProviders,
  HotelRateDisplayPreferences, PageLinks,
  RoomCategoryFilters, SearchModes, SearchStatus, TravelTypes,
  checkDoSearchDeepLinkWithoutDate,
  formatPaymentType,
} from '../../../../../constants';
import AgentCommission from '../../HotelItinerary/AgentCommission';
import HotelCancellationLabel from '../../HotelCancellationLabel';
import Label from '../../../../Label';
import withSpinner from '../../../../../HOCs/withSpinner';
import RateDetail from './RateDetail';
import ReferLabelRoomDescription from '../ReferLabelRoomDescriptionDetail';
import RateDetailDropdownPopover from '../../HotelItinerary/RateDetailDropdownPopover/index.tsx';
import checkDoSkipTripConfiguration from '../../../../../helpers/selectors/checkDoSkipTripConfiguration';
import TripBookButton from '../../../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import useResetSaveTripStatus from '../../../../../hooks/useResetSaveTripStatus';
import getHotelRateToDisplay from '../../../../../helpers/getHotelRateToDisplay.ts';
import getHotelPriceDisplayPreference from '../../../../../helpers/selectors/getHotelPriceDisplayPreference';
import RoomRateDescription from '../../RoomRateDescription';
import currencySymbol from '../../../../../helpers/currencySymbol';
import useScrollToElementById from '../../../../../hooks/useScrollToElementById';
import getElementId from '../../../../../helpers/getElementId';
import DetailedRoomDescription from '../DetailedRoomDescription';
import checkDoShowMarkupRates from '../../../../../helpers/selectors/checkDoShowMarkupRates';
import getHotelRate from '../../../../../helpers/selectors/getHotelRate.ts';
import { getHotelRateCodesRequest } from '../../../../../store/slices/user';
import RoomImagesModal from './RoomImage';
import RateCodeKeys from '../../HotelFilterBar/RateCodeKey';
import CustomSearchBar from '../../../../Input/CustomInput/customSearchBar';
import CustomDropDown from '../../../../Input/CustomInput/customDropDown';
import NightlyPriceDisplay from '../NightlyPriceDisplay';
import { formatRoomDescription, removeCommissionTerms } from '../../../../../helpers/formatters/formatRoomDescription';
import ModalSpinnerSaveTrip from '../../../../ModalSpinner/ModalSpinnerSaveTrip';
import checkDoEnableTripConfigDecoupling from '../../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import formatTripIdForBrochure from '../../../../../helpers/formatters/formatTripIdForBrochure';
import RoomDescription from './RoomDescription';
import getRateActionPlan from '../../../../../helpers/getRateActionPlan';

/// Ensure TripServices.GetFullRawDescription() in backend is updated
/// whenever changes in following method to make sure hotel improved
/// description retrieved from cache when save trip
export const getFullRawDescription = (product) => {
  let description = `${product.rooms[0].desc}\n`;
  if (product.hotelMandatoryFees && product.hotelMandatoryFees.length) {
    description += `\nMandatory Fees: ${_.map(product.hotelMandatoryFees, (m) => `${m.name}: ${m.value}`).join(', ')}`;
  }
  if (product.valueAdds && product.valueAdds.length) {
    description += `\nMeal: ${product.valueAdds.join(',')}`;
  }
  if (product.amenities && product.amenities.length) {
    description += `\nAmenities: ${product.amenities.join(', ')}`;
  }
  if (product.cancellationPolicy) {
    description += `\nCancellation information: ${product.cancellationPolicy}`;
  }
  if (product.additional_info) {
    description += `\n${product.additional_info}`;
  }
  return description;
};

export const getRateNameFromCode = (
  code,
  rates,
  doShowRateCodesFoldedIntoGroupInLabel,
  useSubtext,
  doShowRateCodeGroupName,
) => {
  if (!code || !rates) return undefined;
  const foundRate = rates.find((r) => r.rateCode === code);
  if (doShowRateCodeGroupName && foundRate && foundRate.group) {
    return foundRate.group.groupName;
  }
  if (useSubtext) {
    return foundRate && (foundRate.subText || foundRate.text)
      ? `${foundRate.subText || foundRate.text} ${
        doShowRateCodesFoldedIntoGroupInLabel ? `(${code})` : ''
      }`
      : code;
  }
  return foundRate && foundRate.text
    ? `${foundRate.text} ${
      doShowRateCodesFoldedIntoGroupInLabel ? `(${code})` : ''
    }`
    : code;
};

const UNDEFINED_CATEGORY_LABEL = 'Undefined Category';
const RoomsDetails = ({
  hotelDetails, tripId, onModalClose, doShowPrice, doShowAgentCommission, tripRoomSearchInput,
  isAnyRateFromHotelAddedToTrip,
}) => {
  const classes = useStyles();
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const [isRestrictedRateOpen, setIsRestrictedRateOpen] = useState(false);
  const productsCodesAddingToBrochure = useSelector((state) => state.brochure
    ?.productsCodesAddingToBrochure ?? []);
  const [hotelRateCodes, setHotelRateCodes] = useState();
  const { pageName, segment: segmentIdToUpdate } = router.query;
  const isConfirmationPage = router.route.indexOf('confirmation') > -1;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const { isViewingBrochure, updateBrochurePriceStatus } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const {
    getRoomDetailsStatus = {},
    roomDescriptions, currentRateDetailCodes = {},
  } = useSelector((state) => state.hotel);
  const currentRateDetailCode = currentRateDetailCodes?.[hotelDetails.hotelKey];
  const {
    travelAgency: currentTravelAgency, isLoggedIn,
    email: loggedInUserEmail,
  } = useSelector((state) => state.user);
  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const productsCodesAddedToBrochure = useSelector((store) => store?.brochure
    ?.productsCodesAddedToBrochure ?? []);
  const doAllowBook = brochure && brochure.doAllowBook;

  const doSkipTripConfiguration = useSelector(checkDoSkipTripConfiguration);
  const pricingPreference = useSelector(getHotelPriceDisplayPreference());
  const doShowMarkupRates = useSelector(checkDoShowMarkupRates({
    mode: TravelTypes.hotel, tripId,
  }));
  const {
    saveSendTripEmailStatus, saveSendTripEmailError,
  } = useSelector((store) => store.trip);
  const scrollToElement = useScrollToElementById();
  const isUpdatingBrochurePrice = isViewingBrochure
    && updateBrochurePriceStatus === SearchStatus.SEARCHING;
  const { tripConfirmation } = useSelector((store) => store.trip);
  const selectedTrip = useSelector((store) => store.trip.selectedTrips[isConfirmationPage
    && tripConfirmation && tripConfirmation.tripId ? tripConfirmation.tripId : tripId]);
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const saveTripStatus = useSelector(
    (store) => ((store.trip.selectedTrips[tripId] || {}).saveTripStatus),
  );
  const isChangeHotelPage = pageName === PageLinks.ChangeHotel.pageName;
  const doCheckoutOnSelect = isChangeHotelPage
    || pageName === PageLinks.AddHotel.pageName;
  const doShowBookButton = tripId && doSkipTripConfiguration && !doCheckoutOnSelect;
  const hotelRateCodesFromStore = useSelector((store) => store.user.hotelRateCodes);
  const doHideSpecialHotelRates = currentTravelAgency
    && currentTravelAgency.doHideSpecialHotelRates;
  const { doGroupRoomRates: groupRates, disableMultipleRoomBookings } = (currentTravelAgency
      && currentTravelAgency.settings && currentTravelAgency.settings) || {};
  const doGroupRoomRates = groupRates && !isConfirmationPage;
  const {
    doShowChatGPTImprovedRoomDescription,
    doShowOriginalShorterRoomDescription,
    doAllowShowingRawRateDescription,
    doShowRateCodesFoldedIntoGroupInLabel,
    doShowRateCodeGroupName,
    doAllowDeepLinkWithoutDate,
    selectRoomButtonLabel,
  } = useSelector((store) => {
    const { travelAgency } = store.user;
    const settings = (travelAgency || {}).settings || {};

    return {
      doShowChatGPTImprovedRoomDescription: settings.doShowChatGPTImprovedRoomDescription || false,
      doShowOriginalShorterRoomDescription: settings.doShowOriginalShorterRoomDescription || false,
      doAllowShowingRawRateDescription: settings.doAllowShowingRawRateDescription || false,
      doAllowDeepLinkWithoutDate: settings.doAllowDeepLinkWithoutDate || false,
      doShowRateCodesFoldedIntoGroupInLabel: settings.doShowRateCodesFoldedIntoGroupInLabel
      !== undefined && settings.doShowRateCodesFoldedIntoGroupInLabel !== null
        ? settings.doShowRateCodesFoldedIntoGroupInLabel : true,
      selectRoomButtonLabel: settings.selectRoomButtonLabel || false,
      doShowRateCodeGroupName: settings.doShowRateCodeGroupName || false,
    };
  });

  const redirectToTabletSite = (product) => {
    if (!product || !product.externalBookingUrl) return;
    const url = product.externalBookingUrl;

    track('roomsDetails.redirectToTabletSite', {
      product: JSON.stringify(product),
      loggedInUserEmail,
      travelAgencyId: currentTravelAgency?.id,
      externalUrl: url,
    });

    window.open(url, '_blank');
  };

  const isDeepLinkWithoutDateSearch = useMemo(() => (
    checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate)
  ), [doAllowDeepLinkWithoutDate, router.query]);

  const checkIsSelected = useCallback((item) => {
    if (doShowBookButton) return false;
    if (selectedTrip
      && selectedTrip.hotelItineraries
      && (Object.keys(selectedTrip.hotelItineraries).length > 0)) {
      return Object.values(selectedTrip.hotelItineraries).some(
        (hotelItinerary) => hotelItinerary.selectedRoomCode === item.code,
      );
    }
    return false;
  }, [selectedTrip, doShowBookButton]);

  const handleRemoveFromTrip = useCallback((roomCode) => {
    if (!(selectedTrip
      && selectedTrip.hotelItineraries
      && (Object.keys(selectedTrip.hotelItineraries).length > 0))) {
      return;
    }

    const hotelItinerary = Object.values(selectedTrip.hotelItineraries).find(
      (hi) => hi.selectedRoomCode === roomCode,
    );
    if (!hotelItinerary) return;

    dispatch(removeFromTrip({
      tripId,
      key: 'hotelItineraries',
      id: hotelItinerary.id,
    }));
  }, [selectedTrip, dispatch, tripId]);

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);

  const handleScroll = useCallback((event) => {
    if (!event) return;
    if (doEnableTripConfigDecoupling) {
      const containerWrapper = document.getElementById('main-section-wrapper');
      containerWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const elementId = getElementId(event, '.parentDiv');
    if (elementId) {
      scrollToElement(elementId, 'banner');
    } else {
      scrollToElement(tripId, 'banner');
    }
  }, [doEnableTripConfigDecoupling, scrollToElement, tripId]);

  const handleSelectRoomClick = (event, product) => {
    if (isBrochurePage) {
      dispatch(setSelfBookingAllowed());
    }
    if (tripId) {
      const doShowMarkupAmount = doShowMarkupRates
        && product.roomPrice.markupPrice !== product.roomPrice.originalPrice;

      const newItinerary = {
        ...hotelDetails,
        selectedRoomCode: product.code,
        selectedProduct: product,
        hotel: {
          ...hotelDetails.hotel,
          price: {
            ...hotelDetails.hotel.price,
            doShowMarkupAmount,
          },
        },
        cost: {
          ...hotelDetails.cost,
          doShowMarkupAmount,
        },
        id: +_.uniqueId(),
      };
      const updatedTrip = {
        ...(isBrochurePage ? {} : (selectedTrip || {})),
        selectedRoomCodes: disableMultipleRoomBookings
          ? [product.code]
          : [...(selectedTrip && selectedTrip.selectedRoomCodes
            ? selectedTrip.selectedRoomCodes : []), product.code],
        hotelItineraries: disableMultipleRoomBookings
          ? [newItinerary]
          // eslint-disable-next-line no-nested-ternary
          : [...(isBrochurePage
            ? []
            : selectedTrip?.hotelItineraries
              && Object.keys(selectedTrip.hotelItineraries).length > 0
              ? Object.values(selectedTrip.hotelItineraries)
              : []),
          newItinerary],
      };

      dispatch(addToTrip({
        tripId,
        trip: updatedTrip,
      }));

      if (onModalClose) onModalClose();
      handleScroll(event);
      return updatedTrip;
    }

    track('roomsDetails.handleSelectRoomClick', {
      product: JSON.stringify(product),
    });
    return undefined;
  };

  const handleCheckoutOnRoomClick = (product) => {
    if (saveTripStatus === SearchStatus.SEARCHING) return;

    let tripSegmentId = +segmentIdToUpdate || undefined;
    if (selectedTrip.hotelItineraries && selectedTrip.hotelItineraries.length) {
      if (selectedTrip.hotelItineraries.length === 1) {
        tripSegmentId = selectedTrip.hotelItineraries[0].tripSegmentId;
      } else if (tripSegmentId >= 0) {
        let selectedHotelItinerary = Object.values(selectedTrip.hotelItineraries || {}).find(
          (hotelItinerary) => hotelItinerary.tripSegmentId === tripSegmentId,
        );
        if (!selectedHotelItinerary && selectedTrip.hotelItineraries
            && selectedTrip.hotelItineraries.length) {
          [selectedHotelItinerary] = selectedTrip.hotelItineraries;
        }
        if (selectedHotelItinerary)tripSegmentId = selectedHotelItinerary.tripSegmentId;
      }
    }

    const request = {
      tripId,
      ignoreUpdateTripOnSuccess: true,
      trip: {
        tripId: doShowBookButton ? undefined : tripId,
        selectedRoomCodes: [product.code],
        hotelItineraries: [{
          ...hotelDetails,
          selectedRoomCode: product.code,
          tripSegmentId: isChangeHotelPage ? tripSegmentId : 0,
          id: +_.uniqueId(),
        }],
        doChangeItineraryInBookedTrip: pageName === PageLinks.ChangeHotel.pageName,
        doAddItineraryInBookedTrip: pageName === PageLinks.AddHotel.pageName,
      },
    };

    dispatch(saveTripRequest(request));
    track('roomsDetails.handleCheckoutOnRoomClick', {
      product: JSON.stringify(product),
    });
    if (onModalClose) onModalClose();
  };

  const handleRateDescriptionToggle = (product, isBrochureToggleEvent) => {
    const code = currentRateDetailCode === product.code ? undefined : product.code;
    if (!isBrochureToggleEvent) {
      dispatch(setCurrentRateDetailCode({ hotelKey: hotelDetails.hotelKey, rateCode: code }));
    }

    const {
      doCallGetProductInfo, doCallEnhanceDesc, doAddToBrochure,
    } = getRateActionPlan(product, isBrochureToggleEvent, code,
      currentTravelAgency, roomDescriptions, productsCodesAddedToBrochure);

    if (doCallGetProductInfo) {
      dispatch(getRoomDetailRequest({
        hotelProducts: [product],
        hotelKey: hotelDetails.hotelKey,
        searchKey: hotelDetails.qs,
        hotelProviderId: hotelDetails.hotel.providerId,
        doCallEnhanceDesc,
        doAddToBrochure,
        isAnyRateFromHotelAddedToTrip,
      }));
    } else if ((isConfirmationPage || isBrochurePage) && doCallEnhanceDesc) {
      dispatch(getRoomDescriptionRequest({
        code,
        description: getFullRawDescription(product),
        currencyCode: product.roomPrice.propertyCurrencyCode || product.roomPrice.currencyCode,
      }));
    }
  };

  const showProductPricing = useCallback((product) => {
    const hotelRate = getHotelRate({
      rate: product.roomPrice, doShowMarkupRate: doShowMarkupRates,
    });
    const rateToDisplay = getHotelRateToDisplay(pricingPreference, hotelRate);

    let packageRate = 0;
    if (!doShowMarkupRates) {
      const rate = getHotelRate({
        rate: product.roomPrice, doShowMarkupRate: true,
      });
      packageRate = getHotelRateToDisplay(pricingPreference, rate);
      packageRate = packageRate !== rateToDisplay ? packageRate : 0;
    }
    return (
      <div className={clsx('font-bold text-right', { 'text-sm': !!onModalClose })}>
        {packageRate > 0 && (
          <NumberFormat
            value={packageRate}
            displayType="text"
            thousandSeparator
            prefix={currencySymbol(product.roomPrice.currencyCode)}
            fixedDecimalScale="true"
            decimalScale="2"
            className="line-through inline-block"
          />
        )}
        <NumberFormat
          value={rateToDisplay}
          displayType="text"
          thousandSeparator
          prefix={currencySymbol(product.roomPrice.currencyCode)}
          fixedDecimalScale="true"
          decimalScale="2"
          className="ml-1 block lg:inline-block"
        />
        {pricingPreference === HotelRateDisplayPreferences.TotalRate && <div className="text-sm">Total Stay</div>}
        {pricingPreference !== HotelRateDisplayPreferences.TotalRate
        && <NightlyPriceDisplay pricingPreference={pricingPreference} />}
        {packageRate > 0 && (
          <div className="text-xs text-gray-600 font-normal mb-1">Package discount</div>
        )}
      </div>
    );
  }, [doShowMarkupRates, onModalClose, pricingPreference]);

  useResetSaveTripStatus(doShowBookButton ? saveSendTripEmailStatus : undefined,
    saveSendTripEmailError);

  useEffect(() => {
    if (hotelRateCodesFromStore) {
      setHotelRateCodes(hotelRateCodesFromStore);
    } else {
      dispatch(getHotelRateCodesRequest());
    }
  }, [dispatch, hotelRateCodesFromStore]);

  const handleRestrictedRateClicked = () => {
    setIsRestrictedRateOpen((v) => !v);
  };

  const filterProductsByCategory = useMemo(() => {
    if (!doHideSpecialHotelRates) {
      return {
        productsWithRestrictedRates: [],
        productsWithoutRestrictedRates: hotelDetails.hotel.products,
      };
    }

    const productsWithRestrictedRates = _.filter(hotelDetails.hotel.products || [],
      (product) => _.some(product.rooms || [],
        (room) => _.some(RoomCategoryFilters, (category) => _.includes(room.category, category))));
    const productsWithoutRestrictedRates = _.filter(hotelDetails.hotel.products || [],
      (product) => _.every(product.rooms || [],
        (room) => !_.some(RoomCategoryFilters, (category) => _.includes(room.category, category))));

    return {
      productsWithRestrictedRates,
      productsWithoutRestrictedRates,
    };
  }, [doHideSpecialHotelRates, hotelDetails.hotel.products]);

  const {
    productsWithRestrictedRates,
    productsWithoutRestrictedRates,
  } = filterProductsByCategory;
  const [filteredProducts, setFilteredProducts] = useState(
    !doHideSpecialHotelRates ? hotelDetails.hotel.products : productsWithoutRestrictedRates,
  );
  const productPrices = useMemo(() => productsWithRestrictedRates.map((product) => {
    const hotelRate = getHotelRate({
      rate: product.roomPrice,
      doShowMarkupRate: true,
    });
    const rateToDisplay = getHotelRateToDisplay(0, hotelRate);
    return rateToDisplay;
  }), [productsWithRestrictedRates]);

  const minimumPrice = Math.min(...productPrices);
  const [doshowImagesModal, setDoshowImagesModal] = useState(false);
  const [selectedProductImages, setSelectedProductImages] = useState([]);

  const [bedType, setBedType] = useState('');
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [category, setCategory] = useState('');
  const [ratePlanCode, setRatePlanCode] = useState('');
  const [groupCodes, setGroupCodes] = useState('');
  const [query, setQuery] = useState('');

  const handleCloseImagesModel = () => {
    setDoshowImagesModal(false);
  };

  const handleOpenImagesModel = (images) => {
    setSelectedProductImages(images);
    setDoshowImagesModal(true);
  };

  const hasAnyRoomWithImages = useMemo(() => hotelDetails.hotel.products
    && !!_.find(hotelDetails.hotel.products, (p) => p.rooms[0]?.images
    && p.rooms[0]?.images?.length),
  [hotelDetails.hotel.products]);

  // Extract bed types and room categories
  const allBedTypes = useMemo(() => {
    const bedTypes = new Set();

    hotelDetails.hotel.products.forEach((product) => product.rooms.forEach((room) => {
      if (Array.isArray(room.bedTypes)) { // Check if bedTypes is a valid array
        room.bedTypes.forEach((bt) => bedTypes.add(bt));
      }
    }));

    return [...bedTypes]; // Convert Set to Array
  }, [hotelDetails]);

  const allRoomCategories = useMemo(() => {
    const roomCategories = new Set();
    hotelDetails.hotel.products.forEach((product) => product.rooms.forEach((room) => {
      if (room.category) roomCategories.add(room.category);
    }));
    return [...roomCategories];
  }, [hotelDetails]);

  const allRoomTypes = useMemo(() => {
    const roomTypes = new Set();
    hotelDetails.hotel.products.forEach((product) => product.rooms.forEach((room) => {
      roomTypes.add(room.type || UNDEFINED_CATEGORY_LABEL);
    }));
    return [...roomTypes];
  }, [hotelDetails]);

  const doHideSabreFilters = useMemo(() => hotelDetails?.hotel?.products
    ?.every((product) => product?.externalBookingUrl), [hotelDetails]);

  const allRatePlanCodes = useMemo(() => {
    // Extract rate codes
    const ratePlanCodes = new Set();
    hotelDetails.hotel.products.forEach((product) => {
      product.rooms.forEach((room) => {
        const code = room.ratePlanClientId || room.ratePlanCode;
        if (code) ratePlanCodes.add(code);
      });
    });

    // Map rate plan codes to their corresponding names
    const ratePlanNames = new Set();
    ratePlanCodes.forEach((code) => {
      const match = hotelRateCodes?.find(
        (hotelRate) => hotelRate.rateCode.toLowerCase() === code.toLowerCase(),
      );
      if (!match) return;
      if (doShowRateCodeGroupName && match.group) {
        ratePlanNames.add(match.group.groupName);
      } else {
        ratePlanNames.add(match.text);
      }
    });
    return Array.from(ratePlanNames);
  }, [hotelDetails, hotelRateCodes, doShowRateCodeGroupName]);

  const filterProducts = useCallback(() => {
    // Convert query to lowercase for case-insensitive matching
    const lowerCaseQuery = query.toLowerCase().trim();
    const hotelProducts = !doHideSpecialHotelRates
      ? hotelDetails.hotel.products : productsWithoutRestrictedRates;
    const products = hotelProducts.filter(
      (product) => (!category || product.rooms.some((room) => room.category === category))
      && (!ratePlanCode || product.rooms.some(
        (room) => (doShowRateCodeGroupName ? groupCodes.includes(room.ratePlanCode)
          && groupCodes.includes(room.ratePlanClientId)
          : room.ratePlanCode === ratePlanCode || room.ratePlanClientId === ratePlanCode),
      ))
      && (!bedType || product.rooms.some((room) => room.bedTypes.includes(bedType)))
      && (!selectedRoomType
        || product.rooms.some((room) => (selectedRoomType === UNDEFINED_CATEGORY_LABEL
          ? !room.type : room.type === selectedRoomType)))
      && (product.rooms.some(
        (room) => (room.desc && room.desc.toLowerCase().includes(lowerCaseQuery))
            || (room.category && room.category.toLowerCase().includes(lowerCaseQuery))
            || (room.type && room.type.toLowerCase().includes(lowerCaseQuery))
            || (room.bedTypes && room.bedTypes.some(
              (roomBedType) => roomBedType.toLowerCase().includes(lowerCaseQuery),
            )),
      )),
    );

    setFilteredProducts(products);
  }, [bedType, category, doHideSpecialHotelRates, doShowRateCodeGroupName,
    hotelDetails.hotel.products, productsWithoutRestrictedRates,
    query, ratePlanCode, groupCodes, selectedRoomType]);

  const filterProductsByKeyWord = useCallback(({ keyWord }) => {
    setQuery(keyWord);
  }, []);

  const onSuiteChange = useCallback((roomCategory) => {
    setCategory(roomCategory);
  }, []);

  const onBedChange = useCallback((bed) => {
    setBedType(bed);
  }, []);

  const onSelectedRoomTypeChange = useCallback((bed) => {
    setSelectedRoomType(bed);
  }, []);

  const onRateCodeChange = useCallback((selectedValue) => {
    if (!selectedValue) {
      setRatePlanCode('');
      setGroupCodes([]);
      return;
    }

    if (doShowRateCodeGroupName) {
      const group = hotelRateCodes.find(
        (r) => r.group?.groupName?.toLowerCase() === selectedValue.toLowerCase(),
      );
      if (group && group.group) {
        const codes = hotelRateCodes
          .filter((r) => r.group?.groupName === group.group.groupName)
          .map((r) => r.rateCode);
        setGroupCodes(codes);
        setRatePlanCode(group.group.groupName);
        return;
      }

      setGroupCodes([]);
      setRatePlanCode('');
    } else {
      const matchedRateCode = hotelRateCodes.find(
        (code) => code.text.toLowerCase() === selectedValue.toLowerCase(),
      );
      setRatePlanCode(matchedRateCode ? matchedRateCode.rateCode : '');
      setGroupCodes([]);
    }
  }, [hotelRateCodes, doShowRateCodeGroupName]);

  useEffect(() => {
    filterProducts();
  }, [query, bedType, category, ratePlanCode, groupCodes, filterProducts]);

  const groupRoomsByDescription = (products) => {
    const groupedRooms = {};
    products.forEach((product) => {
      product.rooms.forEach((room) => {
        let description = room.originalFormattedDesc || room.formattedDesc
          || formatRoomDescription(room.originalDesc || room.desc);

        if (!doShowAgentCommission) {
          description = removeCommissionTerms(description);
        }

        if (!groupedRooms[description]) {
          groupedRooms[description] = [];
        }
        groupedRooms[description].push(product);
      });
    });

    return groupedRooms;
  };
  // Group hotel rooms by their long description
  const groupedRoomRates = doGroupRoomRates && filteredProducts
  && groupRoomsByDescription(filteredProducts);

  // Calculate minimum rate of grouped rooms to show 'from {minimum price of rate}'
  const calculateMinimumRoomPrice = (products) => {
    const minPrice = products.reduce((price, product) => {
      if (product.roomPrice) {
        const hotelRate = getHotelRate({
          rate: product.roomPrice,
          doShowMarkupRate: doShowMarkupRates,
        });
        const rateToDisplay = getHotelRateToDisplay(pricingPreference, hotelRate);

        return price === null ? rateToDisplay : Math.min(price, rateToDisplay);
      }
      return price;
    }, null);
    if (minPrice === null) {
      return null;
    }
    if (minPrice % 1 !== 0) {
      return minPrice.toFixed(2); // Ensure two decimal places for non-integer values
    }
    return minPrice;
  };

  const getRemoveLabel = useCallback((product) => {
    if (disableMultipleRoomBookings) return 'Remove';

    if (!selectedTrip || !selectedTrip.hotelItineraries || !Array.isArray(selectedTrip.hotelItineraries) || !selectedTrip.hotelItineraries.length > 0) return 'Remove';
    const selectedProducts = selectedTrip.hotelItineraries
      .filter((x) => x.selectedRoomCode === product.code);
    return `${selectedProducts.length}${selectedProducts.length === 1 ? ' Room' : ' Rooms'} Selected`;
  }, [disableMultipleRoomBookings, selectedTrip]);

  const addRemoveRateToTripButtonSize = useMemo(() => (
    isNumber(selectedTrip || onModalClose ? Button.buttonSizes.XS : undefined)
      ? Button.buttonSizes.XS : Button.buttonSizes.SM), [onModalClose, selectedTrip]);

  const getAddRemoveRateButtonVariant = useCallback((product) => (checkIsSelected(product)
    ? Button.buttonVariants.INLINE : Button.buttonVariants.PRIMARY), [checkIsSelected]);

  const getRedirectButtonLabel = useMemo(() => {
    switch (hotelDetails?.hotel?.priceProvider) {
      case HotelProviders.TABLET:
        return 'Book on Tablet Pro';
      default:
        return 'Reserve';
    }
  }, [hotelDetails]);

  const RoomListItems = (products) => products && products.map((product, index, array) => (
    <List key={product.code}>
      <>
        <ListItem
          classes={{ root: classes.root }}
          style={doGroupRoomRates ? { paddingLeft: '10px' } : undefined}
          className={clsx(
            'break-words',
            classes.mobileGridLayout,
            checkIsSelected(product)
              ? 'border-2 border-theme border-opacity-25 bg-theme bg-opacity-25 padding-10'
              : '',
          )}
        >
          <div className={clsx({ 'sm:col-span-6 col-span-5 md:flex gap-x-3': doShowPrice && doGroupRoomRates, 'sm:col-span-7 col-span-6 md:flex gap-x-3': doShowPrice && !doGroupRoomRates, 'col-span-9': !doShowPrice })}>
            {hasAnyRoomWithImages && (
            <div className="relative">
              {product.rooms && product.rooms[0] && product.rooms[0].images
                  && product.rooms[0].images.length > 0 ? (
                    <>
                      <img
                        src={product.rooms[0].images[0].url}
                        alt={product.rooms[0].images[0].caption}
                        className="email-img w-32 min-w-32"
                      />
                      <button
                        type="button"
                        className="absolute top-0 left-0 bg-black
                      bg-opacity-70 text-white cursor-pointer rounded-xl m-1"
                        title="Click to view hotel room images in modal"
                        onClick={() => handleOpenImagesModel(product.rooms[0].images)}
                      >
                        <BiExpand className="text-white text-md m-1" />
                      </button>
                    </>
                ) : (
                  <Paper className="email-hidden-field flex-shrink-0 w-32 h-16 text-center text-sm bg-gray-300 pt-1">
                    No photo
                  </Paper>
                )}
            </div>
            )}

            <RoomRateDescription
              room={product.rooms && product.rooms[0]}
              doShowRawGDSDescription={!doShowChatGPTImprovedRoomDescription}
              doShowOriginalShorterRoomDescription={doShowOriginalShorterRoomDescription}
              doShowAgentCommission={doShowAgentCommission}
              doGroupRoomRates={doGroupRoomRates}
            />
          </div>
          <div className={clsx('text-right mb-1', doGroupRoomRates ? 'sm:col-span-3 col-span-4' : 'sm:col-span-2 col-span-3')}>
            <div className={`text-right ${doGroupRoomRates ? 'flex items-start gap-3 justify-end flex-no-wrap' : 'inline-block whitespace-no-wrap'}`}>
              {doShowPrice && (
              <div className="font-bold">
                {product && product.roomPrice
                    && withSpinner(() => (!product.roomPrice.isUnavailableRate
                      ? (
                        showProductPricing(product)
                      )
                      : <span className="italic">Room unavailable</span>
                    ), isUpdatingBrochurePrice, 'Retrieving updated price', 'inline', 'xs')}
              </div>
              )}
              {!product.externalBookingUrl ? <></> : (
                <Button
                  size={Button.buttonSizes.XS}
                  onClick={() => redirectToTabletSite(product)}
                  className="rounded-l-2xl border-r-0 focus:outline-none pl-3 pr-3"
                >
                  {getRedirectButtonLabel}
                </Button>
              )}
              {tripId && !isViewingBrochure
              && !isBrochurePage && !doCheckoutOnSelect && !doShowBookButton
              && !product.externalBookingUrl && (
              <div className="flex flex-nowrap">
                {disableMultipleRoomBookings ? <></>
                  : (
                    <Button
                      size={addRemoveRateToTripButtonSize}
                      onClick={() => handleRemoveFromTrip(product.code)}
                      doDiscardRadius
                      className="rounded-l-2xl border-r-0 focus:outline-none pl-3 pr-2"
                      variant={getAddRemoveRateButtonVariant(product)}
                    >
                      -
                    </Button>
                  )}
                <AddButton
                  isSelected={checkIsSelected(product)}
                  onAdd={(event) => { handleSelectRoomClick(event, product); }}
                  onRemove={() => handleRemoveFromTrip(product.code)}
                  addLabel={selectRoomButtonLabel || 'Add to Trip'}
                  removeLabel={getRemoveLabel(product)}
                  buttonSize={selectedTrip || onModalClose
                    ? Button.buttonSizes.XS : undefined}
                  doDiscardRadius={!disableMultipleRoomBookings}
                  className={!disableMultipleRoomBookings ? 'pl-2 pr-2 focus:outline-none' : undefined}
                />
                {disableMultipleRoomBookings ? <></>
                  : (
                    <Button
                      size={addRemoveRateToTripButtonSize}
                      onClick={(event) => { handleSelectRoomClick(event, product); }}
                      doDiscardRadius
                      className="rounded-r-2xl border-l-0 focus:outline-none pl-2 pr-3"
                      variant={getAddRemoveRateButtonVariant(product)}
                    >
                      +
                    </Button>
                  )}
              </div>
              )}
              {tripId && doCheckoutOnSelect && !product.externalBookingUrl && (
              <Button
                onClick={() => handleCheckoutOnRoomClick(product)}
                size={Button.buttonSizes.SM}
              >
                Select
              </Button>
              )}
              {((doShowBookButton && !isViewingBrochure) || (isBrochurePage
                && (doAllowBook || isLoggedIn)))
              && !product.roomPrice.isUnavailableRate && !isUpdatingBrochurePrice
              && !product.externalBookingUrl && (
              <TripBookButton
                tripId={tripId}
                saveTripStatus={saveTripStatus}
                saveSendTripEmailStatus={saveSendTripEmailStatus}
                saveSendTripEmailError={saveSendTripEmailError}
                buttonColorCssClass="bg-button-theme text-button-theme"
                buttonGroupStyles={{ minWidth: 'unset', paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
                onBookClick={() => handleSelectRoomClick(undefined, product)}
                tripRoomSearchInput={tripRoomSearchInput}
              />
              )}
            </div>
          </div>
          <div className="col-span-9 text-left flex justify-between items-center">
            <div className="flex flex-wrap items-center gap-2 m-1">
              {doShowPrice && (
              <>
                <HotelCancellationLabel product={product} />
              </>
              )}
              {product.valueAdds && product.valueAdds.length > 0
                && product.valueAdds.map((v) => (
                  <div key={v} className="flex items-center">
                    <Label text={v} icon={<FaCheck />} level="success" italic inline />
                    &nbsp;&nbsp;
                  </div>
                ))}
              {product.isMatchWithRequestedRatePlanCode
                ? (
                  <div className="flex items-center">
                    <Label
                      text={getRateNameFromCode(product.rooms[0].ratePlanClientId
                        || product.rooms[0].ratePlanCode, hotelRateCodes,
                      doShowRateCodesFoldedIntoGroupInLabel, false, doShowRateCodeGroupName)}
                      icon={!product.isMatchWithRequestedRatePlanCode
                        ? <FaTimes /> : <FaCheck />}
                      level={!product.isMatchWithRequestedRatePlanCode ? 'danger' : 'success'}
                      italic
                      inline
                      descriptionOnHover={`${getRateNameFromCode(product.rooms[0].ratePlanClientId || product.rooms[0].ratePlanCode, hotelRateCodes, doShowRateCodesFoldedIntoGroupInLabel, true, doShowRateCodeGroupName) ? `${getRateNameFromCode(product.rooms[0].ratePlanClientId || product.rooms[0].ratePlanCode, hotelRateCodes, doShowRateCodesFoldedIntoGroupInLabel, true, doShowRateCodeGroupName)} rate` : 'Rate'} code match with your selection!`}
                    />
                    <RateCodeKeys className="flex items-center" icon={<FaRegQuestionCircle className="text-green-600 ml-2" size={20} />} variant="white" filterSpacingClass="py-1 px-3" label="" selectedSearchMode={SearchModes.HOTEL} />
                  </div>
                ) : <></>}
              {product.isPrepaidRate ? (
                <div className="flex items-center">
                  <Label text="Prepaid Rate" level="success" icon={<FaCheck />} italic inline />
                </div>
              ) : ''}
              {!product.isPrepaidRate && product.paymentTypeCode !== undefined
                && product.paymentTypeCode !== null ? (
                  <div className="flex items-center">
                    <Label text={formatPaymentType(product.paymentTypeCode)} icon={<FaCheck />} level="warning" italic inline />
                  </div>
                ) : <></>}
            </div>
            {doShowAgentCommission && (
            <AgentCommission
              agentCommission={product.roomPrice.agentCommission}
              isCommissionable={product.roomPrice.isCommissionable}
              currencyCode={product.roomPrice.currencyCode}
              agentCommissionPercentage={product.roomPrice.agentCommissionPercentage}
              doShowCommissionAsPercentage={!!(currentTravelAgency && currentTravelAgency.settings
                  && currentTravelAgency.settings.doShowHotelComissionInPercentage)}
              className="p-2 rounded-lg bg-orange-200 whitespace-no-wrap text-sm"
            />
            )}
          </div>
          <div className="col-span-9 text-left flex justify-between mt-1">
            <div className="text-left">
              {!productsCodesAddingToBrochure.includes(product.code) && withSpinner(
                () => (
                  <div
                    className="text-base text-theme cursor-pointer z-10 flex items-center"
                    onClick={() => handleRateDescriptionToggle(product)}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRateDescriptionToggle(product);
                      }
                    }}
                    tabIndex="-1"
                  >
                    {currentRateDetailCode === product.code && (
                    <>
                      Hide Full Description
                      <FaChevronUp className="pl-1" />
                    </>
                    )}
                    {currentRateDetailCode !== product.code && (
                    <>
                      View Full Description
                      <FaChevronDown className="pl-1" />
                    </>
                    )}
                  </div>
                ), getRoomDetailsStatus[product.code] === SearchStatus.SEARCHING, 'Getting room rate details...', 'left-inline', 'xs',
              )}
            </div>
            <div className="flex">
              {!isViewingCompare && (
                <HotelAddUpdateToBrochure
                  tripId={formatTripIdForBrochure(tripId,
                    hotelDetails.startDateTime, hotelDetails.endDateTime, staterooms)}
                  hotelItinerary={hotelDetails}
                  product={product}
                  className="col-span-1 text-right"
                  getImprovedRateDescription={() => handleRateDescriptionToggle(product, true)}
                  tripRoomSearchInput={tripRoomSearchInput}
                  productsCodesAddingToBrochure={productsCodesAddingToBrochure}
                />
              )}
              {doShowPrice && (
              <div className="ml-3 inline-block">
                <RateDetailDropdownPopover
                  roomPrice={product.roomPrice}
                  numberOfNights={hotelDetails.duration}
                  popoverAlignClass="right-0"
                  fontSize="text-base"
                  dropdownLabel="View Price Breakdown"
                  tripId={tripId}
                  doShowMarkupRates={doShowMarkupRates}
                />
              </div>
              )}
            </div>
          </div>
          {currentRateDetailCode === product.code && (
          <div className="col-span-9">
            {doShowChatGPTImprovedRoomDescription && !product?.rooms?.[0]?.htmlDescription && (
            <DetailedRoomDescription
              description={roomDescriptions[currentRateDetailCode]}
              rawDescription={getFullRawDescription(product)}
              doAllowShowingRawRateDescription={doAllowShowingRawRateDescription}
              room={product && product.rooms && product.rooms[0]}
              doShowAgentCommission={doShowAgentCommission}
            />
            )}
            {!doShowChatGPTImprovedRoomDescription && !product?.rooms?.[0]?.htmlDescription && (
            <>
              <RateDetail product={product} />
              <ReferLabelRoomDescription
                room={product && product.rooms && product.rooms[0]}
              />
            </>
            )}
            {product?.rooms?.[0]?.htmlDescription && (
              <RoomDescription
                description={product?.rooms?.[0]?.htmlDescription}
              />
            )}
          </div>
          )}
        </ListItem>
        {(doGroupRoomRates || index !== array.length - 1) && <Divider style={doGroupRoomRates ? { paddingLeft: '10px' } : undefined} />}
      </>
    </List>
  ));

  // Visibility flags for each room rate group
  const [visibleGroups, setVisibleGroups] = useState(
    doGroupRoomRates
      ? Object.keys(groupedRoomRates)
        . reduce((acc, description) => ({ ...acc, [description]: false }), {})
      : null,
  );

  // Toggle visibility of room rate group
  const toggleVisibility = (description) => {
    setVisibleGroups((prev) => ({ ...prev, [description]: !prev[description] }));
  };
  return (
    <div>
      {!isDeepLinkWithoutDateSearch && (
      <div className="flex flex-wrap items-center gap-2 w-full mb-3">
        <CustomSearchBar onChange={(value) => filterProductsByKeyWord({ keyWord: value })} />
        {!doHideSabreFilters && <CustomDropDown options={allRatePlanCodes} label="Rate Codes" onChange={onRateCodeChange} noOptionsMessage="No preferred partner rates found" />}
        <CustomDropDown options={allRoomCategories} label="Categories" onChange={onSuiteChange} />
        <CustomDropDown options={allBedTypes} label="Bed Types" onChange={onBedChange} />
        <CustomDropDown options={allRoomTypes} label="Room Types" onChange={onSelectedRoomTypeChange} />
      </div>
      )}
      <div className="flex items-center gap-2">
        {!doGroupRoomRates && (
        <span className="font-bold">
          <span className="font-subHeading text-subHeading">
            {isDeepLinkWithoutDateSearch ? 'Select checkin/checkout dates to view room rates' : 'Rooms and Rates'}
          </span>
        </span>
        )}
        {!doHideSabreFilters && <RateCodeKeys icon={<FaRegQuestionCircle className="text-green-600 mr-1 place-self-center min-w-4" size={20} />} variant="GrayFilterClasses" filterSpacingClass="py-2 px-3" selectedSearchMode={SearchModes.HOTEL} />}
      </div>
      {doGroupRoomRates && Object.entries(groupedRoomRates).map(([key, value], index) => (
        <div key={key} className={doGroupRoomRates ? 'pb-3' : 'pb-2'}>
          <div className={clsx('flex items-center justify-between gap-2', { 'border-b': (visibleGroups || {})[key] || index !== Object.entries(groupedRoomRates).length - 1 }, doGroupRoomRates ? 'border-gray-400 pb-3' : 'border-gray-300 pb-2')}>
            <div>
              <p className="font-bold text-lg">
                {[
                  value[0].rooms[0].type,
                  value[0].rooms[0].bedTypes && value[0].rooms[0].bedTypes.length > 0 ? value[0].rooms[0].bedTypes.join(', ') : '',
                ]
                  .filter(Boolean) // Removes falsy values like null, undefined, or empty strings
                  .join(' - ')}
              </p>
              <p className={clsx({ 'text-gray-600 text-sm my-1': doGroupRoomRates })}>{key}</p>
            </div>
            <div>
              <p>
                <span className="font-bold">
                  from
                  {' '}
                  {currencySymbol(value[0].roomPrice.currencyCode)}
                  {calculateMinimumRoomPrice(value)}
                </span>
              </p>
              {pricingPreference !== HotelRateDisplayPreferences.TotalRate
                && (
                <NightlyPriceDisplay pricingPreference={pricingPreference} />
                )}
              <Button
                size={Button.buttonSizes.SM}
                onClick={() => toggleVisibility(key)}
                className="w-full"
              >
                <span>
                  {visibleGroups && visibleGroups[key] ? 'Hide rates' : `View ${value.length}
                      ${value.length === 1 ? 'rate' : 'rates'}`}
                </span>
              </Button>
            </div>
          </div>
          {/* Conditionally render RoomListItems if the rate group is visible */}
          {visibleGroups && visibleGroups[key] && RoomListItems(value)}
        </div>
      ))}

      {!doGroupRoomRates && (tripConfirmation && tripConfirmation.tripId && !doShowPrice
        ? RoomListItems(_.filter(filteredProducts, (p) => checkIsSelected(p)))
        : (RoomListItems(filteredProducts)))}
      {doShowBookButton && !isViewingBrochure && <ModalSpinnerSaveTrip tripId={tripId} />}

      {!isDeepLinkWithoutDateSearch && (!filteredProducts || !filteredProducts.length)
        ? <div className="mt-4">No rates available.</div> : <></>}

      {productsWithRestrictedRates.length > 0 && !isConfirmationPage && (
        <>
          <br />
          <Divider />
          <div className="flex justify-around">
            <div className="flex gap-1 text-orange-600 cursor-pointer items-center gap-2 font-bold mt-2" onKeyDown={() => { }} onClick={handleRestrictedRateClicked} role="button" tabIndex={0}>
              <p>
                Some rates have been hidden due to restriction criteria
                {' '}
                (
                <NumberFormat
                  value={minimumPrice}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySymbol(productsWithRestrictedRates[0].roomPrice.currencyCode)}
                  fixedDecimalScale="true"
                  decimalScale="2"
                  suffix="+"
                />
                )
              </p>
              {!isRestrictedRateOpen ? <FaChevronDown className="inline-block" /> : <FaChevronUp className="inline-block" />}
            </div>
          </div>
          {isRestrictedRateOpen && RoomListItems(productsWithRestrictedRates)}
        </>
      )}

      <RoomImagesModal
        doShow={doshowImagesModal}
        onClose={handleCloseImagesModel}
        images={selectedProductImages}
      />
    </div>
  );
};

RoomsDetails.propTypes = {
  hotelDetails: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  doShowAgentCommission: PropTypes.bool,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
  isAnyRateFromHotelAddedToTrip: PropTypes.bool,
};

RoomsDetails.defaultProps = {
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  doShowAgentCommission: true,
  tripRoomSearchInput: undefined,
  isAnyRateFromHotelAddedToTrip: false,
};

export default RoomsDetails;
